import Head from 'next/head';
import Link from 'next/link';
import { useBrand } from '@/contexts/brand';

/**
 * Shared authentication layout with animated gradient lines.
 * If the brand has a loginBackground image, it becomes the full-page background
 * with the card floating on top. Otherwise, the clean white layout with lines is used.
 */
const AuthLayout = ({ children, headerRight, showBackToSignIn = false }) => {
  const { brand } = useBrand();
  const primaryColor = brand?.brandConfig?.primaryColor ?? '#6366f1';
  const brandLogo = brand?.brandConfig?.colorBrandLogo ?? null;
  const brandName = brand?.brandName ?? 'UserEvidence Advocacy';
  const loginBackground = brand?.brandConfig?.loginBackground ?? null;

  const hasBackground = !!loginBackground;
  const faviconUrl = brand?.brandConfig?.icon || brand?.brandConfig?.colorBrandLogo || null;

  return (
    <div className="h-screen max-h-screen w-full flex flex-col items-center overflow-hidden relative">
      {faviconUrl && (
        <Head>
          <link rel="icon" href={faviconUrl} />
        </Head>
      )}
      {/* Background: either login image or white with decorative lines */}
      {hasBackground ? (
        <div className="absolute inset-0">
          <img src={loginBackground} alt="" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      ) : (
        <>
          <div className="absolute inset-0 bg-white" />
          {/* Decorative lines and dots (desktop only) */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
            <div className="hidden lg:flex absolute w-px h-full bg-gray-100 left-[20%] z-1" />
            <div className="hidden lg:flex absolute w-px h-full bg-gray-100 right-[20%] z-1" />

            <div className="hidden lg:flex justify-center items-center absolute left-[calc(20%-5.5px)] top-[70.5px] z-3 w-3 h-3 rounded-full bg-white">
              <div className="bg-gray-50 rounded-full w-2 h-2" />
            </div>
            <div className="hidden lg:flex justify-center items-center absolute right-[calc(20%-5.5px)] top-[70.5px] z-3 w-3 h-3 rounded-full bg-white">
              <div className="bg-gray-50 rounded-full w-2 h-2" />
            </div>
            <div className="hidden lg:flex justify-center items-center absolute left-[calc(20%-5.5px)] bottom-[52.5px] z-3 w-3 h-3 rounded-full bg-white">
              <div className="bg-gray-50 rounded-full w-2 h-2" />
            </div>
            <div className="hidden lg:flex justify-center items-center absolute right-[calc(20%-5.5px)] bottom-[52.5px] z-3 w-3 h-3 rounded-full bg-white">
              <div className="bg-gray-50 rounded-full w-2 h-2" />
            </div>

            <div
              className="hidden lg:flex animated-line absolute w-px h-[25%] left-[20%] top-[-25%] z-2"
              style={{ background: `linear-gradient(to bottom, #f5f5f5, ${primaryColor})` }}
            />
            <div
              className="hidden lg:flex animated-line-reverse absolute w-px h-[25%] right-[20%] bottom-[-25%] z-2"
              style={{ background: `linear-gradient(to top, #f5f5f5, ${primaryColor})` }}
            />
          </div>
        </>
      )}

      {/* Header - brand logo centered */}
      <div
        className={`flex items-center justify-center px-6 py-4 lg:py-5 w-full z-10 relative ${hasBackground ? 'border-b border-white/10' : 'border-b border-gray-100'}`}
      >
        <div className="flex items-center">
          {brandLogo ? (
            <img
              data-testid="auth-brand-logo"
              src={brandLogo}
              alt={brandName}
              className={`h-12 w-auto max-w-[240px] object-contain ${hasBackground ? 'brightness-0 invert' : ''}`}
            />
          ) : (
            <span className={`text-lg font-semibold ${hasBackground ? 'text-white' : 'text-gray-900'}`}>
              {brandName}
            </span>
          )}
        </div>

        {(showBackToSignIn || headerRight) && (
          <div className="absolute right-6 lg:right-12 flex items-center">
            {showBackToSignIn && (
              <p className={`text-sm ${hasBackground ? 'text-white/60' : 'text-gray-400'}`}>
                Changed your mind?{' '}
                <Link
                  href="/"
                  className={`text-sm underline-offset-3 font-medium hover:underline ${hasBackground ? 'text-white/90' : 'text-gray-600'}`}
                >
                  Sign in
                </Link>
              </p>
            )}
            {headerRight}
          </div>
        )}
      </div>

      {/* Main content area */}
      <div className="flex-1 flex justify-center items-center z-10 w-full overflow-y-auto">{children}</div>

      {/* Footer */}
      <div
        className={`flex justify-center py-4 lg:py-5 w-full text-xs z-10 ${hasBackground ? 'border-t border-white/10 text-white/50' : 'border-t border-gray-100 text-gray-400'}`}
      >
        Powered by UserEvidence Advocacy
      </div>
    </div>
  );
};

export default AuthLayout;
